import React, { useState } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { useAuthUser } from 'react-auth-kit';
import {
  useRenameConnectedAppMutation,
  useSignOutConnectedAppMutation,
} from 'src/api/mutations/connectedAppsMutations';
import { useAlerts } from 'src/common/AlertManager';
import { CalendarClock, PencilSimple, PlugsConnected } from 'src/common/icons';
import Button from 'src/components/Button';
import Input from 'src/components/Input';
import useModal from 'src/hooks/useModal';
import type { ConnectedApp, ConnectedAppSchedule } from 'src/types/ConnectedApps';
import type { FetchError } from 'src/types/TApiQueries';
import messages, { scopeLabels } from './messages';
import styles from './ConnectedApps.module.scss';

type Props = {
  app: ConnectedApp;
  /** Show whose connection it is (admin views). */
  showOwner?: boolean;
};

/** The schedules a 409 `session_backs_schedules` lists, if that's what this error is. */
function schedulesFromConflict(error: FetchError): ConnectedAppSchedule[] | null {
  const body = (error as any)?.error;
  if ((error as any)?.status === 409 && body?.error === 'session_backs_schedules') {
    return body.schedules ?? [];
  }
  return null;
}

const ConnectedAppRow = ({ app, showOwner }: Props) => {
  const { formatMessage } = useIntl();
  const { showSuccess, showAlert } = useAlerts();
  const [mode, setMode] = useState<'idle' | 'rename' | 'confirm'>('idle');
  const [label, setLabel] = useState(app.label ?? '');
  // Normally from the list. A 409 can still add schedules created since it
  // loaded, so the confirm step always shows the freshest list it has.
  const [schedules, setSchedules] = useState<ConnectedAppSchedule[]>(app.schedules);

  const signOut = useSignOutConnectedAppMutation({
    onSuccess: () => showSuccess(formatMessage(messages.signedOut, { name: app.app_name })),
    onError: (error) => {
      const conflict = schedulesFromConflict(error);
      if (conflict) {
        setSchedules(conflict);
        setMode('confirm');
        return;
      }
      setMode('idle');
      showAlert(formatMessage(messages.actionFailed));
    },
  });

  const rename = useRenameConnectedAppMutation({
    onSuccess: () => {
      setMode('idle');
      showSuccess(formatMessage(messages.renamed));
    },
    onError: () => showAlert(formatMessage(messages.actionFailed)),
  });

  const backsSchedules = schedules.length > 0;
  // Its actions in Agent activity (ui#978); a member's open in their view.
  const myId = useAuthUser()()?.userId;
  const { closeModal } = useModal();
  const activityLink =
    `/insights/agent-activity?agent_kind=${app.is_scheduler ? 'scheduled' : 'external'}` +
    (app.is_scheduler ? '' : `&oauth_session_id=${app.id}`) +
    (app.user_id !== myId ? `&user_id=${app.user_id}` : '');
  const ownName = app.origin_client_name ?? app.client_name;
  const subtitle =
    app.label && app.label !== ownName
      ? formatMessage(messages.via, { name: ownName, client: app.client_name })
      : app.origin_client_name && app.origin_client_name !== app.client_name
      ? formatMessage(messages.via, { name: app.origin_client_name, client: app.client_name })
      : null;

  const usage = [
    formatMessage(messages.connected, { date: moment(app.created_at).format('D MMM YYYY') }),
    app.last_used_at
      ? formatMessage(messages.lastUsed, { when: moment(app.last_used_at).fromNow() })
      : formatMessage(messages.notUsed),
  ].join(' · ');

  return (
    <li className={styles.row} data-test="connected-app-row">
      <div className={styles.rowMain}>
        <span className={styles.icon} aria-hidden="true">
          {app.is_scheduler ? <CalendarClock /> : <PlugsConnected />}
        </span>

        <div className={styles.details}>
          <span className={styles.name}>{app.app_name}</span>
          {subtitle && <span className={styles.meta}>{subtitle}</span>}
          {showOwner && <span className={styles.meta}>{app.user_email}</span>}
          <span className={styles.meta}>{usage}</span>
          <div className={styles.chips}>
            {app.backs_schedules && (
              <span className={styles.scheduleChip}>
                {formatMessage(messages.runsSchedules, { count: app.schedules.length })}
              </span>
            )}
            {app.scopes.map((scope) => (
              <span key={scope} className={styles.chip}>
                {scopeLabels[scope] ?? scope}
              </span>
            ))}
          </div>
        </div>

        {mode === 'idle' && (
          <div className={styles.actions}>
            {/* Closes the modal when this list is in one (Users page). */}
            <Link
              className={styles.activityLink}
              to={activityLink}
              onClick={() => closeModal()}
              data-test="connected-app-activity"
            >
              {formatMessage(messages.viewActivity)}
            </Link>
            <Button
              mode="flat"
              size="small"
              icon={<PencilSimple />}
              aria-label={formatMessage(messages.rename)}
              tooltip={formatMessage(messages.rename)}
              onClick={() => {
                setLabel(app.label ?? '');
                setMode('rename');
              }}
            />
            <Button mode="tertiary" size="small" onClick={() => setMode('confirm')}>
              {formatMessage(messages.signOut)}
            </Button>
          </div>
        )}
      </div>

      {mode === 'rename' && (
        <div className={styles.confirm}>
          <Input
            label={formatMessage(messages.renameLabel)}
            variant="secondary"
            value={label}
            limit={100}
            initialFocus
            onChange={setLabel}
            onEnter={() => rename.mutate({ id: app.id, label })}
          />
          <p>{formatMessage(messages.renameHint)}</p>
          <div className={styles.confirmButtons}>
            <Button size="small" loading={rename.isLoading} onClick={() => rename.mutate({ id: app.id, label })}>
              {formatMessage(messages.save)}
            </Button>
            <Button mode="tertiary" size="small" onClick={() => setMode('idle')} disabled={rename.isLoading}>
              {formatMessage(messages.cancel)}
            </Button>
          </div>
        </div>
      )}

      {mode === 'confirm' && (
        <div className={styles.confirm} role="alertdialog" aria-label={formatMessage(messages.signOut)}>
          {backsSchedules ? (
            <>
              <p>{formatMessage(messages.signOutSchedulesConfirm)}</p>
              <ul>
                {schedules.map((schedule) => (
                  <li key={schedule.id}>
                    <Link to={`/ai/schedules/${schedule.id}`}>{schedule.title}</Link>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p>{formatMessage(messages.signOutConfirm, { name: app.app_name })}</p>
          )}
          <div className={styles.confirmButtons}>
            <Button
              mode="decline"
              size="small"
              loading={signOut.isLoading}
              onClick={() => signOut.mutate({ id: app.id, includeSchedules: backsSchedules })}
            >
              {formatMessage(backsSchedules ? messages.signOutAndStop : messages.signOut)}
            </Button>
            <Button mode="tertiary" size="small" onClick={() => setMode('idle')} disabled={signOut.isLoading}>
              {formatMessage(messages.cancel)}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
};

export default ConnectedAppRow;
